/**
 * Lead Filter Panel Component
 * Provides filtering options for leads list
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Slider,
  Button,
  Divider,
  Collapse,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
} from '@mui/material';
import {
  FilterList,
  Clear,
  Search,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { LeadFilters } from '../types';

interface LeadFilterPanelProps {
  filters: LeadFilters;
  onFiltersChange: (filters: LeadFilters) => void;
  onClear: () => void;
  variant?: 'sidebar' | 'inline';
}

const LeadFilterPanel: React.FC<LeadFilterPanelProps> = ({
  filters,
  onFiltersChange,
  onClear,
  variant = 'sidebar',
}) => {
  const [expanded, setExpanded] = useState(true);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [scoreRange, setScoreRange] = useState<number[]>([
    filters.minScore || 0,
    filters.maxScore || 100,
  ]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFiltersChange({ ...filters, search: searchInput || undefined });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Status options
  const statusOptions = [
    { value: 'NEW', label: 'New' },
    { value: 'CONTACTED', label: 'Contacted' },
    { value: 'QUALIFIED', label: 'Qualified' },
    { value: 'UNQUALIFIED', label: 'Unqualified' },
    { value: 'CONVERTED', label: 'Converted' },
    { value: 'LOST', label: 'Lost' },
  ];

  // Source options
  const sourceOptions = [
    { value: 'LINKEDIN', label: 'LinkedIn' },
    { value: 'WEBSITE', label: 'Website' },
    { value: 'REFERRAL', label: 'Referral' },
    { value: 'EVENT', label: 'Event' },
    { value: 'COLD_CALL', label: 'Cold Call' },
    { value: 'SOCIAL_MEDIA', label: 'Social Media' },
    { value: 'OTHER', label: 'Other' },
  ];

  // Priority options
  const priorityOptions = [
    { value: 'URGENT', label: 'Urgent' },
    { value: 'HIGH', label: 'High' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LOW', label: 'Low' },
  ];

  // Handle filter change
  const handleFilterChange = (key: keyof LeadFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  // Handle score range change
  const handleScoreRangeChange = (event: Event, newValue: number | number[]) => {
    setScoreRange(newValue as number[]);
  };

  // Handle score range commit (after user releases slider)
  const handleScoreRangeCommit = (event: React.SyntheticEvent | Event, newValue: number | number[]) => {
    const [min, max] = newValue as number[];
    onFiltersChange({
      ...filters,
      minScore: min === 0 ? undefined : min,
      maxScore: max === 100 ? undefined : max,
    });
  };

  // Clear all filters
  const handleClearAll = () => {
    setSearchInput('');
    setScoreRange([0, 100]);
    onClear();
  };

  // Count active filters
  const activeFiltersCount = Object.keys(filters).filter(
    (key) => filters[key as keyof LeadFilters] !== undefined
  ).length;

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList />
          <Typography variant="h6">
            Filters
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip label={activeFiltersCount} size="small" color="primary" />
          )}
        </Box>
        {variant === 'inline' && (
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Search Input */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search leads..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput('')}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Divider />

          {/* Status Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || ''}
              label="Status"
              onChange={(e: SelectChangeEvent) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Source Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Source</InputLabel>
            <Select
              value={filters.source || ''}
              label="Source"
              onChange={(e: SelectChangeEvent) => handleFilterChange('source', e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {sourceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Priority Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority || ''}
              label="Priority"
              onChange={(e: SelectChangeEvent) => handleFilterChange('priority', e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {priorityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          {/* AI Score Range */}
          <Box>
            <Typography variant="body2" gutterBottom>
              AI Score Range: {scoreRange[0]} - {scoreRange[1]}
            </Typography>
            <Slider
              value={scoreRange}
              onChange={handleScoreRangeChange}
              onChangeCommitted={handleScoreRangeCommit}
              valueLabelDisplay="auto"
              min={0}
              max={100}
              marks={[
                { value: 0, label: '0' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
            />
          </Box>

          <Divider />

          {/* Clear Filters Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClearAll}
            disabled={activeFiltersCount === 0}
          >
            Clear All
          </Button>
        </Box>
      </Collapse>
    </Box>
  );

  if (variant === 'sidebar') {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        {content}
      </Paper>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      {content}
    </Box>
  );
};

export default LeadFilterPanel;
